import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { GridPaginationModel } from '@mui/x-data-grid-pro'
import { JsonPreviewModal } from 'src/components/payments-ops/JsonPreviewModal'
import { PageHeader } from 'src/components/payments-ops/PageHeader'
import { AssignInvoiceModal } from 'src/components/payments-ops/transactions/AssignInvoiceModal'
import { IncomingTransactionsTable } from 'src/components/payments-ops/transactions/IncomingTransactionsTable'
import { IncomingTransactionsToolbar } from 'src/components/payments-ops/transactions/IncomingTransactionsToolbar'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import {
  AdminFetchMissingInvoiceTransactions,
  AdminRefundTransaction,
} from 'src/constants/actionTypes'
import { PAYMENTS_OPS_DEFAULT_PAGE_SIZE } from 'src/constants/payments-ops/constants'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import type {
  FetchMissingInvoiceTransactionsPayload,
  PaymentsOpsTransaction,
} from 'src/interfaces/transactionsReview'
import {
  getIncomingTransactionsError,
  getTransactionsReviewSubmitting,
} from 'src/selectors/transactionsReview'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { usePartnerNameMap } from 'src/utils/hooks/paymentsOps/usePartnerNameMap'
import { useQueryParamUpdater } from 'src/utils/hooks/paymentsOps/useQueryParamUpdater'
import { pickTransactionDetails } from 'src/utils/paymentsOps/transactionDetails'
import { resolvePartnerName } from 'src/utils/paymentsOps/partnerNames'

const IncomingTransactionsPageComponent = () => {
  const [searchParams] = useSearchParams()
  const updateParams = useQueryParamUpdater()
  const snackbar = useSnackbar()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const nameMap = usePartnerNameMap()

  const listError = useSelector(getIncomingTransactionsError)
  const isSubmitting = useSelector(getTransactionsReviewSubmitting)

  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [detailsTransaction, setDetailsTransaction] = useState<PaymentsOpsTransaction | null>(null)
  const [assigningTransaction, setAssigningTransaction] = useState<PaymentsOpsTransaction | null>(
    null,
  )
  const [refundingTransaction, setRefundingTransaction] = useState<PaymentsOpsTransaction | null>(
    null,
  )

  const partnerId = searchParams.get('partnerId')
  const externalTransactionId = searchParams.get('externalTransactionId') ?? ''
  // Opens pre-filtered to active partners, as Retool did; `activeOnly=false` in the URL clears it.
  const activeOnly = searchParams.get('activeOnly') !== 'false'

  const paginationModel = useMemo(() => {
    const pageSize = Number(searchParams.get('limit')) || PAYMENTS_OPS_DEFAULT_PAGE_SIZE
    const offset = Number(searchParams.get('offset')) || 0

    return { page: Math.floor(offset / pageSize), pageSize }
  }, [searchParams])

  const request = useMemo<FetchMissingInvoiceTransactionsPayload>(() => {
    return {
      partnerId,
      externalTransactionId: externalTransactionId === '' ? null : externalTransactionId,
      activeOnly,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    }
  }, [activeOnly, externalTransactionId, paginationModel, partnerId])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchMissingInvoiceTransactions.request(request))
  }, [asyncDispatchWithErrorToast, isAuthenticated, refreshToken, request])

  const handlePartnerChange = useCallback(
    (nextPartnerId: string | null) => {
      updateParams({ partnerId: nextPartnerId, offset: null })
    },
    [updateParams],
  )

  const handleExternalIdChange = useCallback(
    (value: string) => {
      const trimmedValue = value.trim()

      // TableSearch fires its debounced callback on mount with the unchanged value; treating
      // that as a filter change would drop `offset` and bounce a deep link back to page 1.
      if (trimmedValue === externalTransactionId) {
        return
      }

      updateParams({
        externalTransactionId: trimmedValue === '' ? null : trimmedValue,
        offset: null,
      })
    },
    [externalTransactionId, updateParams],
  )

  const handleActiveOnlyChange = useCallback(
    (nextActiveOnly: boolean) => {
      updateParams({ activeOnly: nextActiveOnly ? null : 'false', offset: null })
    },
    [updateParams],
  )

  const handleClear = useCallback(() => {
    updateParams({ partnerId: null, externalTransactionId: null, activeOnly: null, offset: null })
  }, [updateParams])

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      updateParams({ limit: String(model.pageSize), offset: String(model.page * model.pageSize) })
    },
    [updateParams],
  )

  const handleConfirmRefund = useCallback(async () => {
    if (!refundingTransaction) {
      return
    }

    const result = await asyncDispatchWithErrorToast(
      AdminRefundTransaction.request({ transactionId: refundingTransaction.id, refetch: request }),
    )

    // The modal cannot be dismissed while the request is in flight, so it is still open here.
    setRefundingTransaction(null)

    if (result) {
      snackbar?.enqueueSnackbar('Refund created — it is now in Refunds → Created.', {
        variant: 'success',
      })
    }
  }, [asyncDispatchWithErrorToast, refundingTransaction, request, snackbar])

  const handleCloseRefund = useCallback(() => {
    if (isSubmitting) {
      return
    }

    setRefundingTransaction(null)
  }, [isSubmitting])

  const handleCloseDetails = useCallback(() => {
    setDetailsTransaction(null)
  }, [])

  const handleCloseAssign = useCallback(() => {
    setAssigningTransaction(null)
  }, [])

  const refundPartner = refundingTransaction
    ? resolvePartnerName(nameMap, refundingTransaction.partnerId, refundingTransaction.partnerName)
    : ''

  return (
    <div
      data-testid="incoming-transactions-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <PageHeader
        title="Incoming Transactions"
        description="Incoming money that is not yet matched to an invoice."
      />
      <IncomingTransactionsToolbar
        partnerId={partnerId}
        externalTransactionId={externalTransactionId}
        activeOnly={activeOnly}
        onPartnerChange={handlePartnerChange}
        onExternalTransactionIdChange={handleExternalIdChange}
        onActiveOnlyChange={handleActiveOnlyChange}
        onClear={handleClear}
        onRefresh={handleRefresh}
      />
      {listError ? (
        <Typography variant="body2" css={tw`bg-shades-0 px-4 py-3 text-critical-500`}>
          {listError}
        </Typography>
      ) : null}
      <IncomingTransactionsTable
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        onViewDetails={setDetailsTransaction}
        onAssign={setAssigningTransaction}
        onRefund={setRefundingTransaction}
      />
      <JsonPreviewModal
        title="Transaction details"
        value={detailsTransaction ? pickTransactionDetails(detailsTransaction) : null}
        onClose={handleCloseDetails}
      />
      <AssignInvoiceModal
        transaction={assigningTransaction}
        refetch={request}
        onClose={handleCloseAssign}
      />
      <ConfirmActionModal
        isOpen={refundingTransaction !== null}
        title="Create refund?"
        description={
          refundingTransaction
            ? `Create a refund of ${formatCentsAsUsd(
                refundingTransaction.amount,
              )} to ${refundPartner} for transaction ${
                refundingTransaction.id
              }? This creates a refund in Refunds → Created. No money moves until it is executed there.`
            : ''
        }
        confirmLabel="Create refund"
        isDestructive
        isSubmitting={isSubmitting}
        onConfirm={handleConfirmRefund}
        onClose={handleCloseRefund}
        confirmDataTestId="incoming-refund-confirm"
      />
    </div>
  )
}

export const IncomingTransactionsPage = memo(IncomingTransactionsPageComponent)
